import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { RefreshCw, Filter, Plus, Building2, AlertTriangle, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { fetchWithAuth, getCurrentUser } from '../utils/api';
import useIsMobile from '../hooks/useIsMobile';
import scaleFormats from '../utils/schedulerScale';

const MyCalendar = () => {
  const { print_lang, currentLanguage } = useLanguage();
  const isMobile = useIsMobile();
  const schedulerRef = useRef(null);
  const schedulerInitialized = useRef(false);
  const [schedulerReady, setSchedulerReady] = useState(false);
  const [branches, setBranches] = useState([]);
  const [selectedBranches, setSelectedBranches] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showDayOffModal, setShowDayOffModal] = useState(false);
  // Which of the Day/Week/Month tabs is highlighted. Seeded from the
  // breakpoint so the first paint is not wrong, then kept in step with the
  // scheduler by the breakpoint effect further down.
  const [currentView, setCurrentView] = useState(() => (isMobile ? 'day' : 'week'));
  const [dayOffData, setDayOffData] = useState({
    start_date: '',
    end_date: '',
    text: '',
    branch_id: ''
  });
  const [patientInfo, setPatientInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Memoize user to prevent infinite re-renders
  const user = useMemo(() => getCurrentUser(), []);

  // Branch colors for visual distinction. A categorical set, so the six
  // non-brand entries stay as they are - they exist to be told apart. Only the
  // first was the stock Tailwind blue the palette remap should have caught; it
  // is now --brand-600. Literal hex rather than var(--brand-600) because this
  // array is handed to DHTMLX, which writes it into inline styles on elements
  // it renders itself.
  const branchColors = useMemo(() => ['#2F6B58', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'], []);

  // Format date for scheduler display
  const formatSchedulerDate = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}:00`;
  };

  // Initialize scheduler
  const initScheduler = useCallback(() => {
    if (!window.scheduler || schedulerInitialized.current || !schedulerRef.current) return;

    const scheduler = window.scheduler;

    // Clear any previous instance
    scheduler.clearAll();

    // Basic configuration
    scheduler.config.xml_date = "%Y-%m-%d %H:%i:%s";
    scheduler.config.api_date = "%Y-%m-%d %H:%i:%s";
    scheduler.config.time_step = 15;
    scheduler.config.first_hour = 7;
    scheduler.config.last_hour = 21;
    scheduler.config.start_on_monday = true;
    scheduler.config.scroll_hour = 8;
    scheduler.config.show_loading = true;

    // Disable editing by default (read-only for patient appointments)
    scheduler.config.drag_move = false;
    scheduler.config.drag_resize = false;
    scheduler.config.dblclick_create = false;
    scheduler.config.details_on_create = false;
    scheduler.config.details_on_dblclick = false;

    // Set locale
    if (currentLanguage === 'th') {
      scheduler.locale.labels.day_tab = "วัน";
      scheduler.locale.labels.week_tab = "สัปดาห์";
      scheduler.locale.labels.month_tab = "เดือน";
      scheduler.locale.labels.new_event = "กิจกรรมใหม่";
      scheduler.locale.labels.icon_save = "บันทึก";
      scheduler.locale.labels.icon_cancel = "ยกเลิก";
      scheduler.locale.labels.icon_delete = "ลบ";
    } else {
      scheduler.locale.labels.day_tab = "Day";
      scheduler.locale.labels.week_tab = "Week";
      scheduler.locale.labels.month_tab = "Month";
    }

    // Column headers. DHTMLX divides the container into equal scale cells and
    // never wraps or ellipsises their labels: at 390px "Mon, September 14"
    // needs 123px in a 45px column, so all seven were clipped, and four of the
    // seven month names went the same way. Phones get the abbreviated day name.
    //
    // The width is read live, on every render, rather than from `isMobile`
    // captured here: DHTMLX calls these templates itself, and there are three
    // tiers - the narrowest phones cannot fit the date number at any readable
    // size. See utils/schedulerScale.js.
    scheduler.templates.week_scale_date = function(date) {
      return scheduler.date.date_to_str(scaleFormats(window.innerWidth).week)(date);
    };
    scheduler.templates.month_scale_date = function(date) {
      return scheduler.date.date_to_str(scaleFormats(window.innerWidth).month)(date);
    };

    // Event text template for week/day view
    scheduler.templates.event_text = function(start, end, event) {
      let text = '';
      if (event.patient_name) {
        text = `<strong>${event.patient_name}</strong>`;
        if (event.text) text += `<br/><span style="font-size:11px">${event.text}</span>`;
      } else {
        text = `<strong>${event.text || 'Day Off'}</strong>`;
      }
      if (event.branch_name) {
        text += `<br/><span style="font-size:10px;opacity:0.8">[${event.branch_name}]</span>`;
      }
      return text;
    };

    // Event bar text template for month view
    scheduler.templates.event_bar_text = function(start, end, event) {
      if (event.patient_name) {
        return event.patient_name + (event.branch_name ? ` [${event.branch_name}]` : '');
      }
      return (event.text || 'Day Off') + (event.branch_name ? ` [${event.branch_name}]` : '');
    };

    // Event class template
    scheduler.templates.event_class = function(start, end, event) {
      let classes = [];
      if (!event.patient_id) {
        classes.push('day-off-event');
      }
      if (event.status === 'completed') {
        classes.push('completed-event');
      }
      if (event.status === 'cancelled') {
        classes.push('cancelled-event');
      }
      return classes.join(' ');
    };

    // Handle event click - show patient info
    scheduler.attachEvent("onClick", function(id, e) {
      const event = scheduler.getEvent(id);
      if (event) {
        if (event.patient_id) {
          // Show patient info modal
          setPatientInfo({
            name: event.patient_name,
            phone: event.patient_phone,
            drug_allergy: event.drug_allergy,
            medical_history: event.medical_history,
            type: currentLanguage === 'th' ? (event.type_name_th || event.type_name || event.text) : (event.type_name || event.text),
            branch: event.branch_name,
            status: event.status,
            start: event.start_date,
            end: event.end_date
          });
        }
      }
      return true;
    });

    // Prevent double-click edit
    scheduler.attachEvent("onDblClick", function(id, e) {
      return false; // Prevent default double-click behavior
    });

    // Initialize scheduler. The view is deliberately not chosen from isMobile
    // here - see the breakpoint effect below.
    scheduler.init(schedulerRef.current, new Date(), "week");
    schedulerInitialized.current = true;
    setSchedulerReady(true);

    // console.log('Scheduler initialized successfully');
  }, [currentLanguage]);

  // Load appointments from API
  const loadAppointments = useCallback(async () => {
    if (!window.scheduler || !schedulerInitialized.current) return;

    setIsLoading(true);
    try {
      const data = await fetchWithAuth('/api/dentists/my-appointments');

      if (Array.isArray(data)) {
        window.scheduler.clearAll();

        // Process events with colors based on branch
        const processedEvents = data
          .filter(event => {
            if (selectedBranches.length === 0) return true;
            return selectedBranches.includes(event.branch_id);
          })
          .map(event => {
            // Find branch index for color
            const branchIndex = branches.findIndex(b => b.branch_id === event.branch_id);
            // Same literal-hex reasoning as branchColors above: --brand-600.
            const branchColor = branchIndex >= 0 ? branchColors[branchIndex % branchColors.length] : '#2F6B58';

            // Day off events are gray, patient appointments use branch color
            const eventColor = event.patient_id ? branchColor : '#6b7280';

            return {
              id: event.id,
              text: event.text || '',
              start_date: formatSchedulerDate(event.start_date),
              end_date: formatSchedulerDate(event.end_date),
              patient_id: event.patient_id,
              patient_name: event.patient_name,
              patient_phone: event.patient_phone,
              drug_allergy: event.drug_allergy,
              medical_history: event.medical_history,
              branch_id: event.branch_id,
              branch_name: event.branch_name,
              type_name: event.type_name,
              type_name_th: event.type_name_th,
              status: event.status,
              color: eventColor,
              textColor: '#ffffff'
            };
          });

        window.scheduler.parse(processedEvents, "json");
        // console.log(`Loaded ${processedEvents.length} events`);
      }
    } catch (error) {
      console.error('Failed to load appointments:', error);
    } finally {
      setIsLoading(false);
    }
  }, [selectedBranches, branches, branchColors]);

  // Load user branches - only once on mount
  useEffect(() => {
    if (user?.branches) {
      setBranches(user.branches);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize scheduler after component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      initScheduler();
    }, 200);

    return () => {
      clearTimeout(timer);
      if (schedulerInitialized.current && window.scheduler) {
        // Don't destroy, just clear
        window.scheduler.clearAll();
      }
    };
  }, [initScheduler]);

  // Load appointments after scheduler is initialized
  useEffect(() => {
    if (schedulerReady) {
      loadAppointments();
    }
  }, [schedulerReady, loadAppointments]);

  // Pick the view for the current breakpoint: a phone opens on the day view,
  // because a full week of columns does not fit a 390px screen. Desktop keeps
  // the week view the scheduler has always defaulted to.
  //
  // This is a separate effect on purpose. Putting isMobile in initScheduler's
  // dependency list instead made a tablet rotation across 767px re-run the
  // mount effect, whose cleanup calls scheduler.clearAll() - and the re-run
  // then bailed on the `schedulerInitialized.current` guard without
  // re-parsing anything, so the calendar went blank and stayed blank until the
  // dentist navigated away and back. Switching the view in place touches no
  // events, and is much cheaper than a teardown and rebuild.
  useEffect(() => {
    if (!schedulerReady || !window.scheduler || !schedulerInitialized.current) return;
    const view = isMobile ? 'day' : 'week';
    // Keep the highlighted Day/Week/Month tab honest about what is on screen.
    setCurrentView(view);
    window.scheduler.setCurrentView(null, view);
  }, [isMobile, schedulerReady]);

  // Navigation functions
  const handleToday = () => {
    if (window.scheduler) {
      window.scheduler.setCurrentView(new Date());
    }
  };

  const handlePrev = () => {
    if (window.scheduler) {
      const state = window.scheduler.getState();
      const currentDate = state.date;
      let newDate;

      if (currentView === 'day') {
        newDate = new Date(currentDate);
        newDate.setDate(newDate.getDate() - 1);
      } else if (currentView === 'week') {
        newDate = new Date(currentDate);
        newDate.setDate(newDate.getDate() - 7);
      } else {
        newDate = new Date(currentDate);
        newDate.setMonth(newDate.getMonth() - 1);
      }

      window.scheduler.setCurrentView(newDate);
    }
  };

  const handleNext = () => {
    if (window.scheduler) {
      const state = window.scheduler.getState();
      const currentDate = state.date;
      let newDate;

      if (currentView === 'day') {
        newDate = new Date(currentDate);
        newDate.setDate(newDate.getDate() + 1);
      } else if (currentView === 'week') {
        newDate = new Date(currentDate);
        newDate.setDate(newDate.getDate() + 7);
      } else {
        newDate = new Date(currentDate);
        newDate.setMonth(newDate.getMonth() + 1);
      }

      window.scheduler.setCurrentView(newDate);
    }
  };

  const handleViewChange = (view) => {
    setCurrentView(view);
    if (window.scheduler) {
      window.scheduler.setCurrentView(null, view);
    }
  };

  // Branch filter toggle
  const toggleBranchFilter = (branchId) => {
    setSelectedBranches(prev => {
      if (prev.includes(branchId)) {
        return prev.filter(id => id !== branchId);
      }
      return [...prev, branchId];
    });
  };

  // Handle day off submission
  const handleDayOffSubmit = async (e) => {
    e.preventDefault();

    // Format dates for API
    const formattedData = {
      ...dayOffData,
      start_date: dayOffData.start_date.replace('T', ' ') + ':00',
      end_date: dayOffData.end_date.replace('T', ' ') + ':00'
    };

    try {
      const response = await fetchWithAuth('/api/dentists/day-off', {
        method: 'POST',
        body: JSON.stringify(formattedData)
      });

      if (response.success) {
        setShowDayOffModal(false);
        setDayOffData({ start_date: '', end_date: '', text: '', branch_id: '' });
        loadAppointments();
      }
    } catch (error) {
      console.error('Failed to add day off:', error);
      alert(print_lang('failed_to_add_day_off', 'Failed to add day off'));
    }
  };

  // Format time for display
  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString(currentLanguage === 'th' ? 'th-TH' : 'en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(currentLanguage === 'th' ? 'th-TH' : 'en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="bg-white rounded-lg shadow-sm mb-4 p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          {/* Left side - Navigation */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleToday}
              className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              {print_lang('today', 'Today')}
            </button>
            <div className="flex items-center">
              <button
                onClick={handlePrev}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={handleNext}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Center - View tabs */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            {['day', 'week', 'month'].map(view => (
              <button
                key={view}
                onClick={() => handleViewChange(view)}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors
                  ${currentView === view
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-800'
                  }`}
              >
                {print_lang(`${view}_tab`, view.charAt(0).toUpperCase() + view.slice(1))}
              </button>
            ))}
          </div>

          {/* Right side - Actions */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors
                ${showFilters ? 'bg-blue-50 border-blue-200 text-blue-700' : 'border-gray-200 hover:bg-gray-50'}`}
            >
              <Filter className="w-4 h-4" />
              <span className="hidden sm:inline">{print_lang('filter', 'Filter')}</span>
              {selectedBranches.length > 0 && (
                <span className="bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded-full">
                  {selectedBranches.length}
                </span>
              )}
            </button>

            <button
              onClick={loadAppointments}
              disabled={isLoading}
              className="flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </button>

            <button
              onClick={() => setShowDayOffModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">{print_lang('day_off', 'Day Off')}</span>
            </button>
          </div>
        </div>

        {/* Branch Filters */}
        {showFilters && branches.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-sm text-gray-500 mb-2">{print_lang('filter_by_branch', 'Filter by Branch')}:</p>
            <div className="flex flex-wrap gap-2">
              {branches.map((branch, index) => {
                const isActive = selectedBranches.length === 0 || selectedBranches.includes(branch.branch_id);
                return (
                  <button
                    key={branch.branch_id}
                    onClick={() => toggleBranchFilter(branch.branch_id)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-all
                      ${isActive ? 'text-white shadow-md' : 'bg-gray-100 text-gray-400'}`}
                    style={{
                      backgroundColor: isActive ? branchColors[index % branchColors.length] : undefined
                    }}
                  >
                    <Building2 className="w-3 h-3" />
                    {branch.branch_name}
                  </button>
                );
              })}
              {selectedBranches.length > 0 && (
                <button
                  onClick={() => setSelectedBranches([])}
                  className="px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 hover:underline"
                >
                  {print_lang('show_all', 'Show All')}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Scheduler Container */}
      {/* overflow-auto on a phone: the day view's 7am-9pm grid is taller than
          the space left under the fixed mobile header/bottom bar, so it needs
          to scroll rather than get clipped by overflow-hidden. Desktop keeps
          the original overflow-hidden - its week view already fits. */}
      <div className="flex-1 bg-white rounded-lg shadow-sm overflow-auto md:overflow-hidden" style={{ minHeight: '600px' }}>
        <div
          ref={schedulerRef}
          className="dhx_cal_container"
          style={{ width: '100%', height: '100%' }}
        >
          <div className="dhx_cal_navline">
            <div className="dhx_cal_prev_button">&nbsp;</div>
            <div className="dhx_cal_next_button">&nbsp;</div>
            <div className="dhx_cal_today_button"></div>
            <div className="dhx_cal_date"></div>
          </div>
          <div className="dhx_cal_header"></div>
          <div className="dhx_cal_data"></div>
        </div>
      </div>

      {/* Patient Info Modal */}
      {patientInfo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setPatientInfo(null)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 animate-slideUp" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">{print_lang('appointment_details', 'Appointment Details')}</h3>
              <button
                onClick={() => setPatientInfo(null)}
                className="p-1 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="flex items-center gap-3 pb-3 border-b">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-blue-600 font-bold text-lg">
                    {patientInfo.name?.charAt(0) || '?'}
                  </span>
                </div>
                <div>
                  <p className="font-semibold text-lg">{patientInfo.name || '-'}</p>
                  <p className="text-gray-500">{patientInfo.phone || '-'}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">{print_lang('date', 'Date')}</p>
                  <p className="font-medium">{formatDate(patientInfo.start)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{print_lang('time', 'Time')}</p>
                  <p className="font-medium">{formatTime(patientInfo.start)} - {formatTime(patientInfo.end)}</p>
                </div>
              </div>

              <div>
                <p className="text-sm text-gray-500">{print_lang('treatment_type', 'Type')}</p>
                <p className="font-medium">{patientInfo.type || '-'}</p>
              </div>

              {patientInfo.branch && (
                <div>
                  <p className="text-sm text-gray-500">{print_lang('branch', 'Branch')}</p>
                  <p className="font-medium flex items-center gap-1">
                    <Building2 className="w-4 h-4 text-gray-400" />
                    {patientInfo.branch}
                  </p>
                </div>
              )}

              {patientInfo.drug_allergy && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-red-700 mb-1">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="font-medium">{print_lang('drug_allergy', 'Drug Allergy')}</span>
                  </div>
                  <p className="text-red-600">{patientInfo.drug_allergy}</p>
                </div>
              )}

              {patientInfo.medical_history && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 text-orange-700 mb-1">
                    <AlertTriangle className="w-4 h-4" />
                    <span className="font-medium">{print_lang('medical_history', 'Medical History')}</span>
                  </div>
                  <p className="text-orange-600">{patientInfo.medical_history}</p>
                </div>
              )}
            </div>
            <div className="p-4 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={() => setPatientInfo(null)}
                className="w-full py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
              >
                {print_lang('close', 'Close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Day Off Modal */}
      {showDayOffModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowDayOffModal(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4 animate-slideUp" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">{print_lang('add_day_off', 'Add Day Off')}</h3>
              <button
                onClick={() => setShowDayOffModal(false)}
                className="p-1 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleDayOffSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {print_lang('start_date', 'Start Date/Time')} *
                </label>
                <input
                  type="datetime-local"
                  value={dayOffData.start_date}
                  onChange={(e) => setDayOffData(prev => ({ ...prev, start_date: e.target.value }))}
                  required
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {print_lang('end_date', 'End Date/Time')} *
                </label>
                <input
                  type="datetime-local"
                  value={dayOffData.end_date}
                  onChange={(e) => setDayOffData(prev => ({ ...prev, end_date: e.target.value }))}
                  required
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {print_lang('reason', 'Reason')}
                </label>
                <input
                  type="text"
                  value={dayOffData.text}
                  onChange={(e) => setDayOffData(prev => ({ ...prev, text: e.target.value }))}
                  placeholder={print_lang('day_off_placeholder', 'e.g., Personal leave, Conference...')}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {print_lang('apply_to_branch', 'Apply to Branch')}
                </label>
                <select
                  value={dayOffData.branch_id}
                  onChange={(e) => setDayOffData(prev => ({ ...prev, branch_id: e.target.value }))}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">{print_lang('all_branches', 'All Branches')}</option>
                  {branches.map(branch => (
                    <option key={branch.branch_id} value={branch.branch_id}>
                      {branch.branch_name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {print_lang('day_off_branch_hint', 'Leave empty to block time at all branches')}
                </p>
              </div>
              <div className="flex gap-3 pt-3">
                <button
                  type="button"
                  onClick={() => setShowDayOffModal(false)}
                  className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                >
                  {print_lang('cancel', 'Cancel')}
                </button>
                <button
                  type="submit"
                  className="flex-1 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  {print_lang('save', 'Save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Loading Overlay */}
      {isLoading && (
        <div className="absolute inset-0 bg-white/50 flex items-center justify-center z-10">
          <div className="spinner"></div>
        </div>
      )}
    </div>
  );
};

export default MyCalendar;
